'use client';

import { cn } from '../utils/cn';

/** Subset of Dictionary used by the legal footer (avoids circular imports). */
export type PublicProfileLegalFooterDictionary = {
  publicProfile?: {
    legal?: {
      impressum?: string;
      privacy?: string;
      terms?: string;
      navAria?: string;
    };
  };
};

const LEGAL_URLS = {
  impressum: 'https://www.up2promo.com/policies/legal-notice',
  privacy: 'https://www.up2promo.com/policies/privacy-policy',
  terms: 'https://www.up2promo.com/policies/terms-of-service',
} as const;

type PublicProfileLegalFooterProps = {
  dictionary?: PublicProfileLegalFooterDictionary;
  /**
   * `inline` — after modules in document flow (default).
   * `fixed` / `absolute` — overlay at viewport/parent bottom (legacy).
   */
  position?: 'inline' | 'fixed' | 'absolute';
  className?: string;
};

export function PublicProfileLegalFooter({
  dictionary,
  position = 'inline',
  className,
}: PublicProfileLegalFooterProps) {
  const dict = dictionary?.publicProfile?.legal;
  const impressumLabel = dict?.impressum ?? 'Impressum';
  const privacyLabel = dict?.privacy ?? 'Datenschutzerklärung';
  const termsLabel = dict?.terms ?? 'AGB';

  const isOverlay = position === 'fixed' || position === 'absolute';

  return (
    <footer
      className={cn(
        isOverlay &&
          position === 'absolute' &&
          'absolute bottom-0 left-0 right-0 z-30',
        isOverlay &&
          position === 'fixed' &&
          'fixed bottom-0 left-0 right-0 z-30',
        isOverlay &&
          'pointer-events-none px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4',
        !isOverlay &&
          'w-full shrink-0 px-4 pt-6 pb-[max(1rem,env(safe-area-inset-bottom))]',
        className,
      )}
    >
      <nav
        aria-label={dict?.navAria ?? 'Rechtliches'}
        className={cn(
          'mx-auto flex w-full max-w-2xl flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm text-muted-foreground',
          isOverlay && 'pointer-events-auto',
        )}
      >
        <a
          href={LEGAL_URLS.impressum}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 hover:text-foreground transition-colors"
        >
          {impressumLabel}
        </a>
        <span className="text-border select-none" aria-hidden>
          ·
        </span>
        <a
          href={LEGAL_URLS.privacy}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 hover:text-foreground transition-colors"
        >
          {privacyLabel}
        </a>
        <span className="text-border select-none" aria-hidden>
          ·
        </span>
        <a
          href={LEGAL_URLS.terms}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 hover:text-foreground transition-colors"
        >
          {termsLabel}
        </a>
      </nav>
    </footer>
  );
}
